// hooks/useCacheRefresh.js - YEAR-SPECIFIC REFRESH
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheService } from '../services/cacheService';
import { useToast } from './useToast';

export const useCacheRefresh = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useToast();

  // Finance cache refresh mutation
  const financeRefresh = useMutation({
    mutationFn: async ({ year, endpoint, accountingMethod }) => {
      // STEP 1: Call backend to clear + refresh Cloud Storage cache
      const result = await cacheService.refreshFinanceCache(year, endpoint, accountingMethod);

      // STEP 2: Invalidate (not remove) — keeps showing stale data while refetching
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['revenueChart', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['customerBreakdown', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['financialHealth', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['amiBreakdown', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['customerMetrics', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['cashRunway'] }),
        queryClient.invalidateQueries({ queryKey: ['accountsReceivable'] }),
        queryClient.invalidateQueries({ queryKey: ['financeMaxDates'] }),
      ]);

      return result;
    },
    onMutate: ({ year }) => {
      showInfo(`Refreshing finance data for ${year}...`);
    },
    onSuccess: (data, variables) => {
      const { year } = variables;
      const refreshedCount = data?.details?.endpoints_refreshed?.length || 0;
      showSuccess(`Finance cache refreshed! ${refreshedCount} endpoints updated for ${year}.`);
    },
    onError: (error) => {
      showError('Failed to refresh finance cache. Please try again.');
      console.error('Finance cache refresh error:', error);
    }
  });

  // Playground cache refresh mutation
  const playgroundRefresh = useMutation({
    mutationFn: async ({ year, accountingMethod }) => {
      // STEP 1: Call backend to clear + refresh Cloud Storage cache
      const result = await cacheService.refreshPlaygroundCache(year, accountingMethod);

      // STEP 2: Invalidate — keeps showing stale data while refetching
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const [key] = query.queryKey;
          return key === 'plScenario' || key === 'playground';
        }
      });

      return result;
    },
    onMutate: ({ year }) => {
      showInfo(`Refreshing playground cache for ${year}...`);
    },
    onSuccess: (data, variables) => {
      showSuccess(`Playground cache refreshed for ${variables.year}!`);
    },
    onError: (error) => {
      showError('Failed to refresh playground cache.');
      console.error('Playground cache refresh error:', error);
    }
  });

  // Time Activity cache refresh mutation (uses yearMonth instead of year)
  const timeActivityRefresh = useMutation({
    mutationFn: async ({ yearMonth }) => {
      // STEP 1: Call backend to clear cache
      const result = await cacheService.refreshTimeActivityCache(yearMonth);

      // STEP 2: Invalidate — keeps showing stale data while refetching
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return ['timeActivity', 'overservice', 'productivity',
                  'overserviceAnalysis', 'customerFeeSummary',
                  'cycleData', 'customerBreakdown'].includes(key);
        }
      });

      return result;
    },
    onMutate: ({ yearMonth }) => {
      showInfo(`Refreshing overservice cache for ${yearMonth}...`);
    },
    onSuccess: (data) => {
      const yearMonth = data?.details?.year_month || 'current';
      showSuccess(`Overservice cache refreshed for ${yearMonth}!`);
    },
    onError: (error) => {
      showError('Failed to refresh overservice cache.');
      console.error('Time activity cache refresh error:', error);
    }
  });

  // Sales cache refresh mutation
  const salesRefresh = useMutation({
    mutationFn: async ({ year }) => {
      const result = await cacheService.refreshSalesCache(year);

      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['salesMetrics', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['leadMetrics', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['costROI', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['leadTrends', yearStart, yearEnd], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['sourcePerformance', yearStart, yearEnd], exact: true }),
      ]);

      return result;
    },
    onMutate: ({ year }) => {
      showInfo(`Refreshing sales cache for ${year}...`);
    },
    onSuccess: (data, variables) => {
      showSuccess(`Sales cache refreshed for ${variables.year}!`);
    },
    onError: (error) => {
      showError('Failed to refresh sales cache.');
      console.error('Sales cache refresh error:', error);
    }
  });

  return {
    // Finance
    refreshFinanceCache: financeRefresh.mutate,
    isRefreshingFinance: financeRefresh.isPending,
    
    // Playground
    refreshPlaygroundCache: playgroundRefresh.mutate,
    isRefreshingPlayground: playgroundRefresh.isPending,
    
    // Time Activity
    refreshTimeActivityCache: timeActivityRefresh.mutate,
    isRefreshingTimeActivity: timeActivityRefresh.isPending,

    // Sales
    refreshSalesCache: salesRefresh.mutate,
    isRefreshingSales: salesRefresh.isPending,
    
    // Combined loading state
    isRefreshing: financeRefresh.isPending || playgroundRefresh.isPending || 
                  timeActivityRefresh.isPending || salesRefresh.isPending,
  };
};